/**
 * Page engagement (FLO-1096): what a visitor did during ONE page visit, kept
 * as a single compact summary instead of an event per interaction.
 *
 * What it reads off the host page:
 *   - how long the page was VISIBLE (a hidden tab does not count);
 *   - the deepest scroll, as a percentage of the page;
 *   - the headings (h1 to h3, or a heading-less `section[id]`) that stayed in
 *     view for {@link SECTION_DWELL_MS};
 *   - clicks on links and buttons: the element's own label, and for a link its
 *     path on this site or the HOST of another site, never a query string;
 *   - form submits: the form's id, name or action path. Nothing else of it.
 *
 * What it never reads: a field. No `value`, no text of an `input`, `textarea`,
 * `select` or contenteditable region, no password, and no ARIA widget that
 * holds a choice (a custom select's trigger shows the chosen value as its
 * label). An element inside any of those, inside Floe's own widget, or inside
 * anything marked `data-floe-ignore`, is skipped whole ({@link mayRead}), and
 * a click that lands there is not recorded at all. `data-floe-ignore` on
 * `<html>` or `<body>` excludes the whole page ({@link pageIgnored}). The
 * server redacts and caps again; this file is the first line, not the only
 * one.
 *
 * Cost on a customer's page: three passive document/window listeners (click
 * and submit in the capture phase, scroll), a visibility listener, ONE
 * IntersectionObserver, and no MutationObserver. The scroll handler only arms
 * a timer; the layout read happens at most once per {@link SCROLL_THROTTLE_MS}.
 * A click is described off the event path, in a timer. {@link
 * PageEngagementTracker.snapshot} reads no DOM at all, so sending a summary as
 * the page is hidden does no layout work. Every listener and timer body runs
 * inside {@link quietly}: nothing here may throw into a customer's page.
 *
 * This module only observes. Whether it may run (the site's collection switch
 * AND the visitor's consent) is decided by its one caller, visitor-events.ts.
 */

import { bodyByteLength } from './event-queue';

export const PAGE_ENGAGEMENT_CAPS = {
  /** Visible time on one page. The server drops a larger value. */
  activeMsMax: 24 * 60 * 60_000,
  clicks: 10,
  forms: 5,
  /** Headings and sections watched on one page. */
  observed: 60,
  /** A link's path on this site. */
  pathChars: 200,
  sections: 12,
  /**
   * The whole summary, serialised. floe-api refuses a props object over 2048
   * bytes, and refuses the whole BATCH with it, so the summary is trimmed to
   * fit before it is queued: clicks first, then sections, then forms.
   */
  summaryBytes: 1900,
  /** A section label, a click's text, a form identifier, a UTM value. */
  textChars: 80,
  titleChars: 120,
} as const;

/** A heading counts as read once it has stayed in view this long. */
export const SECTION_DWELL_MS = 3000;
/** The scroll position is read at most this often. */
export const SCROLL_THROTTLE_MS = 250;
/** How long a new page is given to render before its headings are watched
 * and its title is read. An SPA changes the URL before the content. */
export const PAGE_SETTLE_MS = 1000;

/** The opt-out: nothing inside an element carrying it is ever read. */
export const IGNORE_ATTRIBUTE = 'data-floe-ignore';

const EDITABLE = '[contenteditable]:not([contenteditable="false"])';
/**
 * ARIA widgets that hold what the visitor chose or typed. A custom select is
 * a button whose label IS the chosen value; a toggle's label says what was
 * picked. They are form controls in everything but the tag name.
 */
const CHOICE_WIDGETS = [
  'combobox',
  'listbox',
  'option',
  'radio',
  'checkbox',
  'switch',
  'textbox',
  'searchbox',
  'slider',
  'spinbutton',
  'menuitemradio',
  'menuitemcheckbox',
]
  .map((role) => `[role="${role}"]`)
  .join(', ');
const CONTROLS = `input, textarea, select, option, ${EDITABLE}, ${CHOICE_WIDGETS}, [aria-haspopup="listbox"], [aria-checked], [aria-pressed]`;
/** An element inside any of these is never read. */
const NEVER_READ = `${CONTROLS}, [${IGNORE_ATTRIBUTE}], [data-floe-widget]`;
/** An element that CONTAINS one of these gives no text of its own. */
const HOLDS_INPUT = `${CONTROLS}, [${IGNORE_ATTRIBUTE}]`;
const CLICKABLE = 'a[href], button, [role="button"], [role="link"]';
const HEADINGS = 'h1, h2, h3';
const SECTION_MARKS = `${HEADINGS}, section[id]`;

export interface PageClick {
  kind: 'link' | 'button';
  text: string;
  /** Path on this site. Never set together with `host`. */
  path?: string;
  /** Host of a link that leaves this site. */
  host?: string;
}

export interface PageEngagement {
  activeMs: number;
  scrollPercent: number;
  sections: string[];
  clicks: PageClick[];
  forms: string[];
  /** The page title once the page had settled. */
  title?: string;
}

export interface PageEngagementTracker {
  /** Begin a page visit: forget the last one and watch this page. */
  start: () => void;
  /** The visit so far, or null when nothing is being tracked. Reads no DOM. */
  snapshot: () => PageEngagement | null;
  /** Stop watching and unbind everything. */
  stop: () => void;
}

/**
 * Collapse whitespace and cut to `maxChars` on a code-point boundary. Only
 * the head of a long text is looked at: a link around a whole card has far
 * more text than a label needs.
 */
const squash = (text: string, maxChars: number): string => {
  const words = text.trimStart().slice(0, maxChars * 8).split(/\s+/u);
  return [...words.filter(Boolean).join(' ')].slice(0, maxChars).join('');
};

/** May this element be read at all? False inside a form control, an editable
 * region, a choice widget, Floe's own widget, or anything marked
 * `data-floe-ignore`. */
export const mayRead = (element: Element): boolean =>
  element.closest(NEVER_READ) === null;

/** Is the whole page excluded? True when `<html>` or `<body>` carries
 * `data-floe-ignore`: only the page's path is then recorded. */
export const pageIgnored = (): boolean =>
  document.documentElement.hasAttribute(IGNORE_ATTRIBUTE) ||
  document.body?.hasAttribute(IGNORE_ATTRIBUTE) === true;

/**
 * The page title, from the `<title>` element. Not `document.title`: an
 * element named "title" on the host page shadows that property.
 */
export const pageTitle = (): string =>
  squash(
    document.head?.querySelector('title')?.textContent ?? '',
    PAGE_ENGAGEMENT_CAPS.titleChars,
  );

/**
 * Wrap a listener or timer body that runs on a customer's page. Whatever goes
 * wrong inside it stays inside it: it must never reach the host's error
 * handlers.
 */
const quietly =
  <T>(run: (value: T) => void) =>
  (value: T): void => {
    try {
      run(value);
    } catch {
      /* analytics never throws into the host page */
    }
  };

/** {@link quietly}, for a timer or a listener that takes nothing. */
const quietlyRun = (run: () => void) => (): void => {
  try {
    run();
  } catch {
    /* analytics never throws into the host page */
  }
};

/**
 * An element's own label: its `aria-label`, else its text. An element that
 * holds a form control or an editable region gives no text, so a value can
 * never ride out inside a label.
 */
export const labelOf = (element: Element): string => {
  if (!mayRead(element)) {
    return '';
  }
  const named = element.getAttribute('aria-label');
  if (named) {
    return squash(named, PAGE_ENGAGEMENT_CAPS.textChars);
  }
  if (element.querySelector(HOLDS_INPUT)) {
    return '';
  }
  return squash(element.textContent ?? '', PAGE_ENGAGEMENT_CAPS.textChars);
};

/** A heading's text, or the id of a section that has no heading. */
export const sectionLabel = (element: Element): string => {
  if (!element.matches('section')) {
    return labelOf(element);
  }
  if (!mayRead(element) || element.querySelector(HEADINGS)) {
    return '';
  }
  return squash(
    element.getAttribute('id') ?? '',
    PAGE_ENGAGEMENT_CAPS.textChars,
  );
};

/** Where a link goes: its path on this site, or another site's host. A
 * `mailto:` or `tel:` link has neither: its target is a person. */
const linkTarget = (href: string, pageUrl: string): Partial<PageClick> => {
  try {
    const url = new URL(href, pageUrl);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return {};
    }
    return url.host === new URL(pageUrl).host
      ? { path: squash(url.pathname, PAGE_ENGAGEMENT_CAPS.pathChars) }
      : { host: url.hostname };
  } catch {
    return {};
  }
};

/** A click on a link or button, or null when there is nothing to record. */
export const describeClick = (
  element: Element,
  pageUrl: string,
): PageClick | null => {
  if (!mayRead(element)) {
    return null;
  }
  const text = labelOf(element);
  const href = element.matches('a[href]') ? element.getAttribute('href') : null;
  if (href === null) {
    const kind = element.matches('[role="link"]') ? 'link' : 'button';
    return text ? { kind, text } : null;
  }
  const target = linkTarget(href, pageUrl);
  if (!text && !target.path && !target.host) {
    return null;
  }
  return { kind: 'link', text, ...target };
};

/**
 * What identifies a submitted form: its id, else its name, else its action
 * path, else nothing (an empty string still says a form was submitted). Null
 * when the form may not be read. Attributes only: `form.id` and `form.action`
 * can be shadowed by a field of that name, and no field is ever touched.
 */
export const formIdentifier = (
  form: Element,
  pageUrl: string,
): string | null => {
  if (form.closest(`[${IGNORE_ATTRIBUTE}], [data-floe-widget]`)) {
    return null;
  }
  const named = form.getAttribute('id') || form.getAttribute('name');
  if (named) {
    return squash(named, PAGE_ENGAGEMENT_CAPS.textChars);
  }
  const action = form.getAttribute('action');
  if (!action) {
    return '';
  }
  try {
    return squash(
      new URL(action, pageUrl).pathname,
      PAGE_ENGAGEMENT_CAPS.textChars,
    );
  } catch {
    return '';
  }
};

/** Drop from the end (clicks, then sections, then forms) until it fits. */
export const fitSummary = (
  summary: PageEngagement,
  maxBytes: number = PAGE_ENGAGEMENT_CAPS.summaryBytes,
): PageEngagement => {
  const fitted: PageEngagement = {
    ...summary,
    clicks: [...summary.clicks],
    forms: [...summary.forms],
    sections: [...summary.sections],
  };
  const lists = [fitted.clicks, fitted.sections, fitted.forms];
  while (
    bodyByteLength(JSON.stringify(fitted)) > maxBytes &&
    lists.some((list) => list.length > 0)
  ) {
    lists.find((list) => list.length > 0)?.pop();
  }
  return fitted;
};

/** How far down the page the bottom of the viewport has reached, 0 to 100. */
export const scrollPercentOf = (
  scrollTop: number,
  viewportHeight: number,
  pageHeight: number,
): number => {
  const full = Math.max(pageHeight, viewportHeight);
  if (full <= 0) {
    return 0;
  }
  const reached = ((scrollTop + viewportHeight) / full) * 100;
  return Math.max(0, Math.min(100, Math.round(reached)));
};

/**
 * Enough steps that a section many viewports tall still reports back as it
 * fills the screen: with only 0 and 0.5 it would report once, at its edge.
 */
const VIEW_THRESHOLDS = [0, 0.02, 0.05, 0.1, 0.25, 0.5, 0.75, 1];

/** Half of the element, or half of the viewport for one taller than it. */
const inViewEnough = (entry: IntersectionObserverEntry): boolean =>
  entry.isIntersecting &&
  (entry.intersectionRatio >= 0.5 ||
    entry.intersectionRect.height >= (entry.rootBounds?.height ?? 0) * 0.5);

const sameClick = (a: PageClick, b: PageClick): boolean =>
  a.kind === b.kind &&
  a.text === b.text &&
  a.path === b.path &&
  a.host === b.host;

interface VisitState {
  activeMs: number;
  visibleSince: number | null;
  scrollPercent: number;
  sections: string[];
  clicks: PageClick[];
  forms: string[];
  title: string;
  /** Watched elements now in view, and since when. */
  inView: Map<Element, number>;
  /** Watched elements the observer last reported as in view. */
  intersecting: Set<Element>;
}

const newVisit = (visibleSince: number | null): VisitState => ({
  activeMs: 0,
  clicks: [],
  forms: [],
  inView: new Map(),
  intersecting: new Set(),
  scrollPercent: 0,
  sections: [],
  title: '',
  visibleSince,
});

const clearTimer = (timer: ReturnType<typeof setTimeout> | null): null => {
  if (timer !== null) {
    clearTimeout(timer);
  }
  return null;
};

/**
 * One tracker for the page's life. `start()` begins a page visit and
 * `snapshot()` reads it; nothing here sends anything.
 */
export const createPageEngagementTracker = (): PageEngagementTracker => {
  let visit: VisitState | null = null;
  let observer: IntersectionObserver | null = null;
  let bound = false;
  let scrollTimer: ReturnType<typeof setTimeout> | null = null;
  let dwellTimer: ReturnType<typeof setTimeout> | null = null;
  let settleTimer: ReturnType<typeof setTimeout> | null = null;

  const measureScroll = quietlyRun((): void => {
    scrollTimer = null;
    if (visit) {
      visit.scrollPercent = Math.max(
        visit.scrollPercent,
        scrollPercentOf(
          globalThis.scrollY,
          globalThis.innerHeight,
          document.documentElement.scrollHeight,
        ),
      );
    }
  });

  const onScroll = (): void => {
    scrollTimer ??= setTimeout(measureScroll, SCROLL_THROTTLE_MS);
  };

  const markRead = (element: Element): void => {
    observer?.unobserve(element);
    if (!visit || visit.sections.length >= PAGE_ENGAGEMENT_CAPS.sections) {
      return;
    }
    const label = sectionLabel(element);
    if (label && !visit.sections.includes(label)) {
      visit.sections.push(label);
    }
    // The list is full: nothing more can be added, so stop watching.
    if (visit.sections.length >= PAGE_ENGAGEMENT_CAPS.sections) {
      observer?.disconnect();
      visit.inView.clear();
      visit.intersecting.clear();
    }
  };

  const checkDwell = quietlyRun((): void => {
    dwellTimer = null;
    if (!visit) {
      return;
    }
    const now = Date.now();
    const due = [...visit.inView].filter(
      ([, since]) => now - since >= SECTION_DWELL_MS,
    );
    for (const [element] of due) {
      visit.inView.delete(element);
      visit.intersecting.delete(element);
      markRead(element);
    }
    const waits = [...visit.inView.values()].map(
      (since) => since + SECTION_DWELL_MS - now,
    );
    if (waits.length > 0) {
      dwellTimer = setTimeout(checkDwell, Math.max(0, Math.min(...waits)));
    }
  });

  const onIntersect = quietly((entries: IntersectionObserverEntry[]): void => {
    if (!visit) {
      return;
    }
    for (const entry of entries) {
      if (inViewEnough(entry)) {
        visit.intersecting.add(entry.target);
        if (visit.visibleSince !== null && !visit.inView.has(entry.target)) {
          visit.inView.set(entry.target, Date.now());
        }
      } else {
        visit.intersecting.delete(entry.target);
        visit.inView.delete(entry.target);
      }
    }
    dwellTimer ??= setTimeout(checkDwell, SECTION_DWELL_MS);
  });

  /** Once the page has rendered: read its title, measure where the visitor
   * already is, and start watching its headings. */
  const settle = quietlyRun((): void => {
    settleTimer = null;
    if (!visit) {
      return;
    }
    visit.title = pageTitle();
    measureScroll();
    if (!globalThis.IntersectionObserver) {
      return;
    }
    observer = new IntersectionObserver(onIntersect, {
      threshold: VIEW_THRESHOLDS,
    });
    // Stop at the cap: a page with thousands of headings must not have each
    // one read just to be thrown away.
    let watched = 0;
    for (const element of document.querySelectorAll(SECTION_MARKS)) {
      if (watched >= PAGE_ENGAGEMENT_CAPS.observed) {
        break;
      }
      if (sectionLabel(element)) {
        observer.observe(element);
        watched += 1;
      }
    }
  });

  const onVisibility = quietlyRun((): void => {
    if (!visit) {
      return;
    }
    const now = Date.now();
    if (document.visibilityState === 'visible') {
      visit.visibleSince ??= now;
      for (const element of visit.intersecting) {
        visit.inView.set(element, now);
      }
      if (visit.inView.size > 0) {
        dwellTimer ??= setTimeout(checkDwell, SECTION_DWELL_MS);
      }
      return;
    }
    // Hidden: bank the visible time, and stop every dwell clock. Only time
    // the page could be seen counts, for the page and for its sections.
    if (visit.visibleSince !== null) {
      visit.activeMs += now - visit.visibleSince;
      visit.visibleSince = null;
    }
    visit.inView.clear();
    dwellTimer = clearTimer(dwellTimer);
  });

  const onClick = quietly((event: Event): void => {
    const origin = event.target instanceof Element ? event.target : null;
    // A click that lands inside a field, a choice widget or an ignored region
    // is not recorded at all, even when a link or button wraps that region.
    if (!origin || !visit || !mayRead(origin)) {
      return;
    }
    const clicked = origin.closest(CLICKABLE);
    if (!clicked) {
      return;
    }
    const tracked = visit;
    const pageUrl = location.href;
    // Off the event path: the host page's own click handlers run first.
    setTimeout(
      quietlyRun((): void => {
        const click = describeClick(clicked, pageUrl);
        if (
          click &&
          tracked.clicks.length < PAGE_ENGAGEMENT_CAPS.clicks &&
          !tracked.clicks.some((seen) => sameClick(seen, click))
        ) {
          tracked.clicks.push(click);
        }
      }),
      0,
    );
  });

  const onSubmit = quietly((event: Event): void => {
    if (!visit || !(event.target instanceof Element)) {
      return;
    }
    const identifier = formIdentifier(event.target, location.href);
    if (
      identifier !== null &&
      visit.forms.length < PAGE_ENGAGEMENT_CAPS.forms
    ) {
      visit.forms.push(identifier);
    }
  });

  const passive = { capture: true, passive: true } as const;

  const bind = (): void => {
    if (bound) {
      return;
    }
    bound = true;
    document.addEventListener('click', onClick, passive);
    document.addEventListener('submit', onSubmit, passive);
    document.addEventListener('visibilitychange', onVisibility);
    globalThis.addEventListener('scroll', onScroll, { passive: true });
  };

  const unwatch = (): void => {
    observer?.disconnect();
    observer = null;
    scrollTimer = clearTimer(scrollTimer);
    dwellTimer = clearTimer(dwellTimer);
    settleTimer = clearTimer(settleTimer);
  };

  return {
    snapshot: () => {
      if (!visit) {
        return null;
      }
      const running =
        visit.visibleSince === null ? 0 : Date.now() - visit.visibleSince;
      const summary: PageEngagement = {
        activeMs: Math.min(
          Math.round(visit.activeMs + running),
          PAGE_ENGAGEMENT_CAPS.activeMsMax,
        ),
        clicks: visit.clicks,
        forms: visit.forms,
        scrollPercent: visit.scrollPercent,
        sections: visit.sections,
      };
      if (visit.title) {
        summary.title = visit.title;
      }
      return fitSummary(summary);
    },
    start: () => {
      if (!globalThis.document) {
        return;
      }
      unwatch();
      visit = newVisit(
        document.visibilityState === 'visible' ? Date.now() : null,
      );
      bind();
      settleTimer = setTimeout(settle, PAGE_SETTLE_MS);
    },
    stop: () => {
      unwatch();
      visit = null;
      if (bound) {
        bound = false;
        document.removeEventListener('click', onClick, passive);
        document.removeEventListener('submit', onSubmit, passive);
        document.removeEventListener('visibilitychange', onVisibility);
        globalThis.removeEventListener('scroll', onScroll);
      }
    },
  };
};
